import { Link } from '@mui/material';
import { DataGridProProps } from '@mui/x-data-grid-pro';
import { t } from 'i18next';
import { BlankCell } from '../../../common/components/Cells/BlankCell';
import { FilterConfiguration } from '../../../common/components/FilterPanel/types';
import { TypographyOverflow } from '../../../common/components/TypographyOverflow';
import { PaginatedTableController, PaginatedTableState, PrefilledDataConfig } from '../../../common/controllers/PaginatedTableController';
import { DefaultHeaderConfiguration, FieldConfiguration } from '../../../common/types/paginated-table';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { PATHS } from '../../../routing/routes';
import { OperationKey } from '../models/valueObjects/OperationKey';
import { DEFAULT_OPERATION_KEYS_PAGE_SIZE, OperationRepository } from '../services/OperationRepository';

type State = PaginatedTableState & {};

type Props = {
  reportId: number;
  operationId: number;
  initialData: PaginationResult<OperationKey> | null;
  parentLoading: boolean;
};

export class OperationKeysTable extends PaginatedTableController<OperationKey, Props, State> {
  private readonly repository = new OperationRepository();

  componentWillUnmount() {
    super.componentWillUnmount();

    this.repository.cancelAllRequests();
  }

  protected getPrefilledConfig() {
    return {
      prefillActive: true,
      initialLoading: this.props.parentLoading,
      initialData: this.props.initialData,
    } as PrefilledDataConfig<OperationKey>;
  }

  protected getDefaultPageSize(): number {
    return DEFAULT_OPERATION_KEYS_PAGE_SIZE;
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [];
  }

  protected getDefaultHeaderConfigurations(): DefaultHeaderConfiguration {
    return { title: t('keys') };
  }

  protected getAdditionalDataGridProps(): Partial<DataGridProProps<OperationKey>> {
    return {
      ...super.getAdditionalDataGridProps(),
      getRowId: (model) => `${model.getId()}_${model.getRole()}`,
    };
  }

  protected getFieldConfigurations(): FieldConfiguration<OperationKey>[] {
    const { reportId } = this.props;
    return [
      {
        fieldKey: 'id',
        label: t('id'),
        description: t('id'),
        columnProps: { maxWidth: 100 },
        render: (model, { tabIndex }) => (
          <Link
            tabIndex={tabIndex}
            href={UrlHelper.generatePath(PATHS.REPORT_KEY_LIFE_CYCLE, { reportId: reportId.toString(), keyId: model.getId().toString() })}
            aria-label={t('a11y.report.operations.keyIdLabel', { keyId: model.getId(), role: model.getRole() }) as string}
          >
            {model.getId()}
          </Link>
        ),
      },
      {
        fieldKey: 'shortDescription',
        label: t('type'),
        description: t('type'),
        render: (model, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{model.getShortDescription()}</TypographyOverflow>,
      },
      {
        fieldKey: 'role',
        label: t('keyRole'),
        description: t('keyRole'),
        columnProps: { maxWidth: 300 },
        render: (model, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{model.getRole()}</TypographyOverflow>,
      },
      {
        fieldKey: 'cipherData',
        label: t('encryptionPayload'),
        description: t('encryptionPayload'),
        columnProps: { maxWidth: 300 },
        render: (model, { hasFocus }) => {
          const data = model.getCipherData();
          if (data) {
            return <TypographyOverflow hasFocus={hasFocus}>{data}</TypographyOverflow>;
          }
          return <BlankCell blankText={t('none')} />;
        },
      },
      {
        fieldKey: 'wrappedKeyId',
        label: t('wrappedKey'),
        description: t('wrappedKey'),
        columnProps: { maxWidth: 200 },
        render: (model, { tabIndex }) => {
          const id = model.getWrappedKeyId();
          if (!id) {
            return <BlankCell blankText={t('none')} />;
          }
          return (
            <Link
              tabIndex={tabIndex}
              href={UrlHelper.generatePath(PATHS.REPORT_KEY_LIFE_CYCLE, { reportId: reportId.toString(), keyId: id.toString() })}
              aria-label={t('a11y.report.operations.wrappedKeyIdLabel', { keyId: id.toString() }) as string}
            >
              {id}
            </Link>
          );
        },
      },
      {
        fieldKey: 'wrappedByKeyId',
        label: t('wrappedByKey'),
        description: t('wrappedByKey'),
        columnProps: { maxWidth: 200 },
        render: (model, { tabIndex }) => {
          const id = model.getWrappedByKeyId();
          if (!id) {
            return <BlankCell blankText={t('none')} />;
          }
          return (
            <Link
              tabIndex={tabIndex}
              href={UrlHelper.generatePath(PATHS.REPORT_KEY_LIFE_CYCLE, { reportId: reportId.toString(), keyId: id.toString() })}
              aria-label={t('a11y.report.operations.wrappedByKeyIdLabel', { keyId: id.toString() }) as string}
            >
              {id}
            </Link>
          );
        },
      },
    ];
  }

  protected async fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<OperationKey>> {
    try {
      return await this.repository.getOperationKeys(this.props.operationId, cursorInfo);
    } catch (e: unknown) {
      this.handleError(e);
      return BLANK_PAGINATION_RESULT;
    }
  }
}
